import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Stack } from '@mui/material';
import { closeSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useNotify } from '@/hooks/useNotify';

import { BulkActionType } from '../constants';
import { useBulkActualizarCobrado } from '../hooks/useBulkActualizarCobrado';
import { useBulkActualizarFacturado } from '../hooks/useBulkActualizarFacturado';
import { useBulkCerrar } from '../hooks/useBulkCerrar';
import type { BulkResponseConsolidada, Protocolo } from '../types';
import { BulkActionDialog } from './BulkActionDialog';
import { BulkFailuresDialog } from './BulkFailuresDialog';
import { ProtocolosBulkActionsBar } from './ProtocolosBulkActionsBar';

interface Operation {
  actionType: BulkActionType;
  uids: string[];
  phase: 'confirm' | 'progress';
  processed: number;
  total: number;
}

interface FailuresData {
  actionType: BulkActionType;
  response: BulkResponseConsolidada;
}

interface ProtocolosBulkActionsProps {
  items: Protocolo[];
  selectedUids: Set<string>;
  getEstablecimientoName: (uid: string) => string;
  onClearSelection: () => void;
}

function getSuccessMessage(actionType: BulkActionType, n: number): string {
  switch (actionType) {
    case BulkActionType.FACTURADO:
      return `${n} protocolos marcados como facturados`;
    case BulkActionType.NO_FACTURADO:
      return `${n} protocolos marcados como no facturados`;
    case BulkActionType.COBRADO:
      return `${n} protocolos marcados como cobrados`;
    case BulkActionType.NO_COBRADO:
      return `${n} protocolos marcados como no cobrados`;
    case BulkActionType.CERRAR:
      return `${n} protocolos cerrados. Informes finales generados`;
  }
}

export function ProtocolosBulkActions({
  items,
  selectedUids,
  getEstablecimientoName,
  onClearSelection,
}: ProtocolosBulkActionsProps): JSX.Element {
  const notify = useNotify();
  const bulkFacturado = useBulkActualizarFacturado();
  const bulkCobrado = useBulkActualizarCobrado();
  const bulkCerrar = useBulkCerrar();

  const [operation, setOperation] = useState<Operation | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const dialogVisibleRef = useRef(false);
  useEffect(() => {
    dialogVisibleRef.current = dialogVisible;
  }, [dialogVisible]);

  const [failuresOpen, setFailuresOpen] = useState(false);
  const [failuresData, setFailuresData] = useState<FailuresData | null>(null);

  const selected = useMemo(
    () => items.filter((p) => selectedUids.has(p.uid_protocolo)),
    [items, selectedUids],
  );

  const protocolosByUid = useMemo(() => {
    const map = new Map<string, Protocolo>();
    for (const p of items) map.set(p.uid_protocolo, p);
    return map;
  }, [items]);

  function handleAction(actionType: BulkActionType): void {
    const uids = selected.map((p) => p.uid_protocolo);
    setOperation({ actionType, uids, phase: 'confirm', processed: 0, total: uids.length });
    setDialogVisible(true);
  }

  function handleCancelConfirm(): void {
    setOperation(null);
    setDialogVisible(false);
  }

  function handleBackground(): void {
    setDialogVisible(false);
  }

  function openFailuresFromSnackbar(): void {
    setFailuresOpen(true);
  }

  async function runOperation(actionType: BulkActionType, uids: string[]): Promise<void> {
    setOperation({ actionType, uids, phase: 'progress', processed: 0, total: uids.length });
    setDialogVisible(true);
    dialogVisibleRef.current = true;

    function onProgress(processed: number, total: number): void {
      setOperation((prev) => (prev && prev.actionType === actionType ? { ...prev, processed, total } : prev));
    }

    let result: BulkResponseConsolidada;
    switch (actionType) {
      case BulkActionType.FACTURADO:
        result = await bulkFacturado.run({ uids, facturado: true, onProgress });
        break;
      case BulkActionType.NO_FACTURADO:
        result = await bulkFacturado.run({ uids, facturado: false, onProgress });
        break;
      case BulkActionType.COBRADO:
        result = await bulkCobrado.run({ uids, cobrado: true, onProgress });
        break;
      case BulkActionType.NO_COBRADO:
        result = await bulkCobrado.run({ uids, cobrado: false, onProgress });
        break;
      case BulkActionType.CERRAR:
        result = await bulkCerrar.run({ uids, onProgress });
        break;
    }

    const wasForeground = dialogVisibleRef.current;

    setOperation(null);
    setDialogVisible(false);
    onClearSelection();

    if (result.fallidos.length === 0) {
      notify.success(getSuccessMessage(actionType, result.exitosos.length));
    } else if (result.exitosos.length === 0) {
      notify.error(
        `La operación falló. ${result.fallidos.length} protocolos no se procesaron — ver detalle.`,
        {
          persist: true,
          action: (key) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button color="inherit" size="small" onClick={openFailuresFromSnackbar}>
                Ver detalle
              </Button>
              <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ),
        },
      );
    } else {
      notify.warning(
        `${result.exitosos.length} protocolos actualizados. ${result.fallidos.length} fallaron — ver detalle.`,
        {
          persist: true,
          action: (key) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button color="inherit" size="small" onClick={openFailuresFromSnackbar}>
                Ver detalle
              </Button>
              <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ),
        },
      );
    }

    if (result.fallidos.length > 0) {
      setFailuresData({ actionType, response: result });
      if (wasForeground) setFailuresOpen(true);
    }
  }

  function handleRetry(uids: string[]): void {
    const actionType = failuresData?.actionType;
    setFailuresOpen(false);
    if (actionType && uids.length > 0) void runOperation(actionType, uids);
  }

  return (
    <>
      <ProtocolosBulkActionsBar selected={selected} onAction={handleAction} onCancel={onClearSelection} />

      {operation && (
        <BulkActionDialog
          open={dialogVisible}
          phase={operation.phase}
          actionType={operation.actionType}
          count={operation.uids.length}
          processed={operation.processed}
          total={operation.total}
          onConfirm={() => void runOperation(operation.actionType, operation.uids)}
          onCancel={handleCancelConfirm}
          onBackground={handleBackground}
        />
      )}

      {failuresData && (
        <BulkFailuresDialog
          open={failuresOpen}
          result={failuresData.response}
          protocolosByUid={protocolosByUid}
          getEstablecimientoName={getEstablecimientoName}
          onClose={() => setFailuresOpen(false)}
          onRetry={handleRetry}
        />
      )}
    </>
  );
}
